import { CircleHelp, Plus, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Campaign } from '../../api/types';
import { useLoad } from '../../lib/useLoad';
import { type AutomationOpen, AutomationPage, CampaignAutomations } from './CampaignAutomations';
import { AUTOMATION_PRESETS } from './automationPresets';
import { CampaignBuilder } from './CampaignBuilder';
import { CampaignGuide } from './CampaignGuide';
import { CampaignResults } from './CampaignResults';
import { CampaignsHome } from './CampaignsHome';
import { ShowChannelBar } from './channelBar';
import { useComms } from './context';
import { CampaignSettings } from './CampaignSettings';
import type { SettingsSection } from './campaignSettingsTools';

type View = 'list' | 'builder' | 'results' | 'automations' | 'automation' | 'settings' | 'guide';

/**
 * Campaigns: two sections (the campaigns themselves, and the automations that
 * send on their own), with Guide and Settings beside them — and a campaign's
 * own page, the builder or its results, which is not a section at all but a
 * place a row opens, headed by the way back to where it was opened from.
 */
export function CampaignsView({
  builderNonce,
  onOpenAnalytics,
}: {
  builderNonce: number;
  /**
   * The Analytics Campaigns card — the other end of a campaign's tracked links.
   * Absent where this mount cannot reach Analytics, and then nothing offers it.
   */
  onOpenAnalytics?: () => void;
}) {
  const { source, capabilities, sendDenied } = useComms();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<View>('list');
  const [resultId, setResultId] = useState<number | null>(null);
  // The draft the builder is editing, if any. A draft has no results to show —
  // reopening it in the builder is the only thing a click on its row can mean.
  const [editing, setEditing] = useState<Campaign | null>(null);
  // Which step the builder opens on. Step 1 for everything an operator is
  // composing; step 4 for a resend, whose audience and copy were both decided
  // before the draft existed and whose only remaining question is Send.
  const [builderStep, setBuilderStep] = useState(1);
  /**
   * "Try one, sent only to me" — a fresh builder with the walkthrough running.
   * The nonce is in the builder's key so every start is a blank campaign, while
   * ending the walkthrough leaves the key alone and the campaign on screen.
   */
  const [touring, setTouring] = useState(false);
  const [tourNonce, setTourNonce] = useState(0);
  /**
   * A campaign's own page says where it was opened from and what it is — the
   * breadcrumb in place of the section header. `back` is where its first link
   * goes: the list, Automations, or (for a run opened from an automation's own
   * page) that automation, so a run read from there returns there.
   */
  const [crumb, setCrumb] = useState<{ back: 'list' | 'automations' | 'automation'; label: string }>({
    back: 'list',
    label: 'New campaign',
  });
  // The automation whose page is open, and the key that remounts it on each open.
  const [automationOpen, setAutomationOpen] = useState<AutomationOpen | null>(null);
  const [automationKey, setAutomationKey] = useState(0);
  // Which Settings section is open, and the mailbox a Senders row asked Sender
  // health to bring into view.
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('senders');
  const [settingsFocus, setSettingsFocus] = useState<number | null>(null);

  // Permissive while /meta is in flight, like every capability read on this module.
  const level = capabilities?.access.features.campaigns ?? 'full';
  const mayDraft = (level === 'draft' || level === 'full') && !sendDenied;

  const openBuilder = useCallback((draft: Campaign | null, step: number) => {
    setEditing(draft);
    setBuilderStep(step);
    setCrumb({ back: 'list', label: draft?.name || 'New campaign' });
    setView('builder');
  }, []);

  // The tab-strip "New campaign" button bumps the nonce to land here in builder
  // mode — on a blank builder, never on whatever draft was last opened.
  useEffect(() => {
    if (builderNonce === 0) return;
    // Deliberate sync flip on the parent's signal — same contract as a prop.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    openBuilder(null, 1);
  }, [builderNonce, openBuilder]);

  /**
   * Where a campaign opens: a draft into the builder, a scheduled one (and a
   * scheduled one that never started) onto Review & send, everything else onto
   * its results. One function, because a row, the attention strip, the Guide
   * and a link in from Analytics must all land a campaign in the same place.
   */
  const openCampaign = useCallback(
    (campaign: Campaign) => {
      if (campaign.status === 'draft') {
        openBuilder(campaign, 1);
        return;
      }
      /**
       * A scheduled campaign has no results — it has resolved nobody and
       * mailed nobody — so the builder is the only screen with anything to
       * show. It opens on Review & send, where the time, the way to move it
       * and the way to call it off all are; the steps behind are still
       * walkable, because it edits exactly like the draft it still is.
       * Likewise a scheduled send that never started: a results page for a
       * campaign with no recipient rows is an empty funnel and no reason.
       */
      if (campaign.status === 'scheduled' || (campaign.status === 'failed' && !campaign.startedAt)) {
        openBuilder(campaign, 4);
        return;
      }
      setResultId(campaign.id);
      setCrumb({ back: campaign.automationId ? 'automations' : 'list', label: campaign.name });
      setView('results');
    },
    [openBuilder],
  );

  /**
   * `?campaign=<id>` — a campaign named from outside this tab: the Analytics
   * Campaigns card opens the campaign whose tracked links a row counts. Spent
   * on arrival (dropped from the address in place), so a reload or Back does
   * not reopen what the reader has since closed; then routed like a row click,
   * which needs the row itself, so it is read first. One that cannot be read
   * leaves the list where it was.
   */
  const linkedCampaign = params.get('campaign');
  useEffect(() => {
    if (linkedCampaign === null) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('campaign');
        return next;
      },
      { replace: true },
    );
    const id = Number(linkedCampaign);
    if (!Number.isInteger(id) || id <= 0) return;
    let cancelled = false;
    source.api.getCampaign(id).then(
      (campaign) => {
        if (!cancelled) openCampaign(campaign);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [linkedCampaign, setParams, source, openCampaign]);

  const startNew = () => openBuilder(null, 1);

  const startTest = () => {
    setTourNonce((n) => n + 1);
    setTouring(true);
    openBuilder(null, 1);
  };

  /** The Automations section, or — given one — that automation's own page. */
  const openAutomations = (open: AutomationOpen | null) => {
    if (!open) {
      setView('automations');
      return;
    }
    setAutomationOpen(open);
    setAutomationKey((n) => n + 1);
    setView('automation');
  };

  const openSettings = (section: SettingsSection = settingsSection, inboxId?: number) => {
    setSettingsSection(section);
    setSettingsFocus(inboxId ?? null);
    setView('settings');
  };

  /** A campaign named by id from Settings (an opt-out's source) — routed like a row. */
  const openCampaignById = (id: number) => {
    source.api.getCampaign(id).then(openCampaign, () => {});
  };

  const openRun = (campaignId: number, back: 'automations' | 'automation') => {
    setResultId(campaignId);
    setCrumb({ back, label: 'Automation run' });
    setView('results');
  };

  const automationLabel = !automationOpen
    ? 'Automation'
    : automationOpen.id === null
      ? automationOpen.preset
        ? AUTOMATION_PRESETS[automationOpen.preset].name
        : 'New automation'
      : automationOpen.name || 'Automation';

  // The Guide decides whether anything has gone out from the newest page.
  const guideList = useLoad(
    () => (view === 'guide' ? source.api.listCampaigns(1) : Promise.resolve(null)),
    [source, view === 'guide'],
  );

  const onPage = view === 'builder' || view === 'results' || view === 'automation';
  // The links before the page's own name: where it was opened from, and for a
  // run read off an automation's page, that automation too.
  const trail: { label: string; go: () => void }[] =
    view === 'automation'
      ? [{ label: 'Automations', go: () => openAutomations(null) }]
      : crumb.back === 'list'
        ? [{ label: 'Campaigns', go: () => setView('list') }]
        : crumb.back === 'automations'
          ? [{ label: 'Automations', go: () => openAutomations(null) }]
          : [
              { label: 'Automations', go: () => openAutomations(null) },
              { label: automationLabel, go: () => setView('automation') },
            ];

  return (
    <div className="cmg">
      {onPage ? (
        <nav className="cmg-crumb" aria-label="Breadcrumb">
          {/* This tab has no rail, and the dashboard can open it with the
              channel bar already folded — so the way back rides here too. */}
          <ShowChannelBar />
          {trail.map((link) => (
            <span key={link.label} className="cmg-crumb-link">
              <button type="button" className="cmg-crumb-back" onClick={link.go}>
                {link.label}
              </button>
              <span aria-hidden>/</span>
            </span>
          ))}
          <span className="cmg-crumb-here">{view === 'automation' ? automationLabel : crumb.label}</span>
        </nav>
      ) : (
        <div className="cmg-subnav cmg-head">
          <ShowChannelBar />
          <h1 className="cmg-title">Campaigns</h1>
          <nav className="cmg-tabs" aria-label="Campaign sections">
            <button
              type="button"
              className={`cmg-tab${view === 'list' ? ' on' : ''}`}
              aria-current={view === 'list' ? 'page' : undefined}
              onClick={() => setView('list')}
            >
              Campaigns
            </button>
            {/* Messages that send themselves. Their runs are ordinary campaigns,
                opened like any other, but listed under their automation rather
                than among the campaigns — a busy one launches a run every
                quarter hour. */}
            <button
              type="button"
              className={`cmg-tab${view === 'automations' ? ' on' : ''}`}
              aria-current={view === 'automations' ? 'page' : undefined}
              onClick={() => openAutomations(null)}
            >
              Automations
            </button>
          </nav>
          <div style={{ flex: 1 }} />
          {/* Help rather than a section: read once, start to finish, by somebody
              about to send their first campaign — and then by whoever inherits
              the job. The first-visit screen carries it too. */}
          <button
            type="button"
            className={`cbtn${view === 'guide' ? ' on' : ''}`}
            onClick={() => setView('guide')}
          >
            <CircleHelp size={13} />
            Guide
          </button>
          <button
            type="button"
            className={`cbtn cmg-settings-btn${view === 'settings' ? ' on' : ''}`}
            onClick={() => openSettings()}
          >
            <SlidersHorizontal size={13} />
            Settings
          </button>
          <button
            type="button"
            className="cbtn cbtn-primary"
            disabled={!mayDraft}
            title={mayDraft ? undefined : 'Your role cannot write campaigns'}
            onClick={() => (view === 'automations' ? openAutomations({ id: null }) : startNew())}
          >
            <Plus size={13} />
            {view === 'automations' ? 'New automation' : 'New campaign'}
          </button>
        </div>
      )}

      {view === 'list' && (
        <CampaignsHome
          mayDraft={mayDraft}
          onOpen={openCampaign}
          onNew={startNew}
          onTryTest={startTest}
          onGuide={() => setView('guide')}
          onSettings={(section, inboxId) => openSettings(section, inboxId)}
          onOpenAutomation={openAutomations}
          onAutomations={() => openAutomations(null)}
        />
      )}

      {view === 'builder' && (
        // Keyed on the draft: the builder seeds its fields once, so opening a
        // different draft (or New campaign) has to remount it.
        <CampaignBuilder
          key={editing ? `draft-${editing.id}` : `new-${tourNonce}`}
          draft={editing}
          initialStep={builderStep}
          tour={touring && !editing}
          onTourEnd={() => setTouring(false)}
          onLaunched={(campaign) => {
            setTouring(false);
            setEditing(null);
            setBuilderStep(1);
            setResultId(campaign.id);
            setCrumb({ back: 'list', label: campaign.name });
            setView('results');
          }}
          onSavedDraft={() => {
            setTouring(false);
            setEditing(null);
            setBuilderStep(1);
            setView('list');
          }}
        />
      )}

      {view === 'results' && resultId !== null && (
        <CampaignResults
          campaignId={resultId}
          // A resend arrives as a draft, so it goes where every other draft
          // goes: the builder, opened on its review step, one press from going
          // out (or from going to review, for an author who cannot send).
          onResendDraft={(draft) => openBuilder(draft, 4)}
          onOpenAnalytics={onOpenAnalytics}
        />
      )}

      {view === 'automations' && (
        <CampaignAutomations onOpen={openAutomations} onOpenRun={(id) => openRun(id, 'automations')} />
      )}

      {view === 'automation' && automationOpen && (
        <AutomationPage
          key={automationKey}
          open={automationOpen}
          onClose={() => openAutomations(null)}
          onOpenRun={(id) => openRun(id, 'automation')}
        />
      )}

      {view === 'settings' && (
        <CampaignSettings
          section={settingsSection}
          focusInboxId={settingsFocus}
          onSection={(section, inboxId) => openSettings(section, inboxId)}
          onOpenCampaign={openCampaignById}
        />
      )}

      {view === 'guide' && (
        <CampaignGuide
          campaigns={guideList.data ?? []}
          onNewCampaign={startNew}
          onTryTest={startTest}
          onOpenCampaign={openCampaign}
          onOpenAnalytics={onOpenAnalytics}
        />
      )}
    </div>
  );
}
